import React, { useState, useEffect } from 'react';
import { notificationService } from '../services/apiClient';
import { formatDistanceToNow } from 'date-fns';
import { Bell, Check, Trash2, X } from 'lucide-react';

const NotificationDropdown = ({ isOpen, onClose }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await notificationService.getNotifications();
            // Backend returns { notifications, unreadCount }
            setNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await notificationService.markAsRead(id);
            setNotifications(prev =>
                prev.map(n => n._id === id ? { ...n, isRead: true } : n)
            );
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const deleteNotification = async (id) => {
        try {
            await notificationService.deleteNotification(id);
            setNotifications(prev => prev.filter(n => n._id !== id));
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-4 border-b border-gray-50 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <Bell className="w-4 h-4 text-purple-600" />
                    Notifications
                </h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="max-h-96 overflow-y-auto">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
                ) : notifications.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No notifications yet</div>
                ) : (
                    notifications.map((n) => (
                        <div
                            key={n._id}
                            className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition relative group ${!n.isRead ? 'bg-purple-50/30' : ''}`}
                        >
                            <div className="flex justify-between items-start gap-2">
                                <p className={`text-sm ${!n.isRead ? 'text-gray-900 font-semibold' : 'text-gray-600'}`}>
                                    {n.message}
                                </p>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                    {!n.isRead && (
                                        <button
                                            onClick={() => markAsRead(n._id)}
                                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                                            title="Mark as read"
                                        >
                                            <Check className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => deleteNotification(n._id)}
                                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400 mt-1 uppercase font-bold tracking-wider">
                                {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                            </p>
                        </div>
                    ))
                )}
            </div>

            {notifications.length > 0 && (
                <div className="p-3 bg-gray-50 text-center border-t border-gray-100">
                    <button
                        onClick={async () => {
                            await notificationService.markAllAsRead();
                            fetchNotifications();
                        }}
                        className="text-xs font-bold text-purple-600 hover:text-purple-700 transition"
                    >
                        Mark all as read
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
